import { useMemo, useState } from 'react';
import { Button, Card, CardContent } from '@tark/shared/components';
import {
  useAuth,
  useScheduleDashboard,
  useTenantBootstrap,
} from '@tark/shared/hooks';
import type {
  ScheduleCoachDashboardAttentionItem,
} from '@tark/shared/types';
import { ListChecks, Loader2, XCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import SessionAttendancePanel from '../components/coach-studio/SessionAttendancePanel';
import CoachPageHeader from '../components/CoachPageHeader';

const MANAGE_ROLES = ['OWNER', 'ADMIN', 'COACH', 'STAFF'];

export default function CoachAttendancePage() {
  const { user } = useAuth();
  const { activeTenant } = useTenantBootstrap(Boolean(user));
  const isCoachStudio = activeTenant?.tenant?.tenantArchetype === 'COACHING';
  const canManage = MANAGE_ROLES.includes(activeTenant?.roleCode?.toUpperCase() || '');
  const dashboard = useScheduleDashboard(Boolean(user) && isCoachStudio);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState<number | null>(null);
  const requestedOccurrenceId = Number(searchParams.get('occurrenceId'));
  const queryOccurrenceId = Number.isFinite(requestedOccurrenceId) && requestedOccurrenceId > 0 ? requestedOccurrenceId : null;

  const attendanceItems = useMemo(
    () => (dashboard.data?.coachDashboard?.attentionItems ?? []).filter(isAttendanceAttention),
    [dashboard.data?.coachDashboard?.attentionItems]
  );
  const upcoming = dashboard.data?.upcomingOccurrences ?? [];
  const nextSessions = upcoming;
  const effectiveOccurrenceId =
    selectedOccurrenceId ??
    queryOccurrenceId ??
    attendanceItems.find((item) => item.occurrenceId)?.occurrenceId ??
    nextSessions[0]?.occurrenceId ??
    null;
  const selectOccurrence = (occurrenceId: number) => {
    setSelectedOccurrenceId(occurrenceId);
    setSearchParams({ occurrenceId: String(occurrenceId) }, { replace: true });
  };

  if (!isCoachStudio || !canManage) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card variant="default">
          <CardContent className="space-y-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <ListChecks className="h-6 w-6" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Coach Studio</p>
              <h1 className="mt-2 text-2xl font-semibold tracking-tight text-text">Attendance needs a Coach Studio</h1>
              <p className="mt-2 text-sm text-text-muted">
                Switch into an enabled coach workspace to review session rosters and mark attendance.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (dashboard.isLoading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (dashboard.isError) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card variant="default">
          <CardContent className="space-y-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-500/10 text-red-500">
              <XCircle className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-text">Attendance unavailable</h1>
              <p className="mt-2 text-sm text-text-muted">Refresh and we will restore the attendance view.</p>
            </div>
            <Button variant="outline" onClick={() => dashboard.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <div className="pro-coach-page w-full space-y-5 pb-24 md:pb-8">
    <CoachPageHeader title="Session & attendance" subtitle={activeTenant?.tenant?.displayName} />
    <label className="pro-search max-w-none"><span className="sr-only">Session</span><select aria-label="Session" className="w-full min-w-0 border-0 bg-transparent text-sm text-text" value={effectiveOccurrenceId || ''} onChange={e=>selectOccurrence(Number(e.target.value))}>
      {!effectiveOccurrenceId && <option value="">Choose a session</option>}
      {effectiveOccurrenceId && !upcoming.some(o=>o.occurrenceId===effectiveOccurrenceId) && <option value={effectiveOccurrenceId}>Session #{effectiveOccurrenceId}</option>}
      {upcoming.map(o=><option key={o.occurrenceId} value={o.occurrenceId}>{o.title} · {formatDateRange(o.startsAt,o.endsAt)}</option>)}
    </select></label>
    {effectiveOccurrenceId ? <SessionAttendancePanel key={effectiveOccurrenceId} occurrenceId={effectiveOccurrenceId} /> : <Card><CardContent><p className="text-sm text-text-muted">No sessions yet.</p></CardContent></Card>}
  </div>;
}

function isAttendanceAttention(item: ScheduleCoachDashboardAttentionItem) {
  const text = `${item.key} ${item.title} ${item.description} ${item.actionKey}`.toLowerCase();
  return text.includes('attendance') || text.includes('check-in') || text.includes('roster');
}

function formatDateRange(startsAt: string, endsAt: string): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const day = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const startTime = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const endTime = end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} / ${startTime} - ${endTime}`;
}
